import { inject, Injectable, InjectionToken } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot } from '@angular/router';
import {
  ObjectWorkflowIntegrationFormField,
  WorkflowIntegrationFormTable,
  WorkflowIntegrationResponse,
  WorkflowIntegrationStep,
} from '@cassiopeia/integration';
import { isTruthy, requiredValue } from '@nestech/utils';
import { SelfServiceWorkflowApi, withFormFieldTypeFallback } from '../danish-pension';

/**
 * Overrides the self-service action name for a wizard mounted at a fixed path. A wizard mounted at
 * `self-service/wizards/:actionName` needs no provider — it takes the name from the URL.
 */
export const WORKFLOW_WIZARD = new InjectionToken<string>('WORKFLOW_WIZARD');

/** The route parameter that names the self-service action a generic wizard runs */
export const WORKFLOW_WIZARD_ACTION_PARAM = 'actionName';

const WorkflowWizardStepTypes = ['intro', 'form', 'sign', 'receipt', 'overlay'] as const;

export type WorkflowWizardStepType = (typeof WorkflowWizardStepTypes)[number];
export const WorkflowWizardStepTypeArray: WorkflowWizardStepType[] = [...WorkflowWizardStepTypes];

/** Translation keys the wizard renders: side-menu label, page heading, page description */
export interface WorkflowWizardStepInfo {
  title: string;
  name?: string;
  description?: string;
}

export interface WorkflowWizardRequiredDocument {
  name: string;
  title: string;
  subtitle?: string;
  placeHolder?: string;
}

export interface WorkflowWizardSigningInfo {
  description: string;
  error: string;
  signingFileName: string;
  signingInProgressTitle?: string;
  signingInProgressDescription?: string;
}

/** Copy for one of the step's form tables, naming the `formTables` entry it belongs to */
export interface WorkflowWizardTableInfo {
  tableName: string;
  title?: string;
  addRowLabel?: string;
  order?: number;
}

export type WorkflowInfoBoxTheme = 'info' | 'warning' | 'danger';

/**
 * A notice the client renders alongside a step or a field. `title` and `description` are
 * `cas.*`/`label.*` keys, or literal text (which may contain HTML) when they start with neither.
 */
export interface WorkflowInfoBox {
  description: string;
  title?: string;
  theme?: WorkflowInfoBoxTheme;
  /** On a field: the box shows while the field holds this value. Absent — always shown. */
  value?: unknown;
  /** On a step: position among field groups, tables and info cards. */
  order?: number;
}

export interface WorkflowWizardStepMetaData {
  stepType: WorkflowWizardStepType;
  info: WorkflowWizardStepInfo;
  hidden?: boolean;
  imageSrc?: string;
  nextStepTitle?: string;
  previousStepTitle?: string;
  requiredDocuments?: WorkflowWizardRequiredDocument[];
  tables?: WorkflowWizardTableInfo[];
  signing?: WorkflowWizardSigningInfo;
  signedDocumentFileName?: string;
  infoBoxes?: WorkflowInfoBox[];
}

export type WorkflowWizardFieldType =
  | 'string'
  | 'multilineString'
  | 'boolean'
  | 'date'
  | 'number'
  | 'currency'
  | 'email'
  | 'phone'
  | 'vehicle'
  | 'radio';

export interface WorkflowWizardFieldMetaData {
  title: string;
  subtitle?: string;
  placeHolder?: string;
  order: number;
  group?: string;
  triggersPatch?: boolean;
  infoBoxes?: WorkflowInfoBox[];
}

export interface WorkflowWizardFormField extends ObjectWorkflowIntegrationFormField {
  type: WorkflowWizardFieldType;
  hidden?: boolean;
  metaData: WorkflowWizardFieldMetaData;
}

export interface WorkflowWizardFormTable extends Omit<WorkflowIntegrationFormTable, 'formFields'> {
  formFields: Record<string, WorkflowWizardFormField>;
}

export interface WorkflowWizardStep extends Omit<WorkflowIntegrationStep, 'formFields' | 'formTables' | 'metaData'> {
  formFields?: Record<string, WorkflowWizardFormField>;
  formTables?: Record<string, WorkflowWizardFormTable>;
  metaData: WorkflowWizardStepMetaData;
}

export interface WorkflowWizard extends WorkflowIntegrationResponse {
  wizardSteps: WorkflowWizardStep[];
}

export type WorkflowWizardFormFieldWithKey = WorkflowWizardFormField & { key: string };

const stepTypes: WorkflowWizardStepType[] = WorkflowWizardStepTypeArray;

/**
 * Step ids the wizard reserves for its own routes, so a step cannot claim one. A step's URL is
 * `<type>/<stepId>` with a bare type segment, so a stepId equal to a type name would make
 * `selectedStep`'s last-two-segments matching ambiguous.
 */
const reservedStepIds: string[] = ['sorry', ...stepTypes];

const isVisibleStep = (step: WorkflowIntegrationStep): boolean => {
  const metaData = step.metaData as WorkflowWizardStepMetaData | undefined;
  if (!metaData?.stepType || metaData.hidden) return false;
  if (!stepTypes.includes(metaData.stepType)) {
    console.warn('workflow step has an unknown stepType and is not rendered', step.stepId, metaData.stepType);
    return false;
  }
  if (!metaData.info?.title) {
    console.warn('workflow step is missing metaData.info.title and is not rendered', step.stepId);
    return false;
  }
  if (reservedStepIds.includes(step.stepId)) {
    console.warn('workflow step uses a reserved stepId and is not rendered', step.stepId);
    return false;
  }
  return true;
};

const toWizardStep = (step: WorkflowIntegrationStep): WorkflowWizardStep => ({
  ...step,
  formFields: step.formFields as Record<string, WorkflowWizardFormField> | undefined,
  formTables: step.formTables as Record<string, WorkflowWizardFormTable> | undefined,
  metaData: step.metaData as WorkflowWizardStepMetaData,
});

/**
 * Raw workflow response to the wizard model: visible steps only, in the order the backend gave.
 * Applies the `dataType` fallback itself so the model is complete for a caller that bypasses
 * `SelfServiceWorkflowApi`.
 */
export const toWorkflowWizardModel = (api: WorkflowIntegrationResponse): WorkflowWizard => {
  const normalized = withFormFieldTypeFallback(api);
  const wizardSteps = Object.values(normalized.steps ?? {})
    .filter(isVisibleStep)
    .toSorted((a, b) => a.order - b.order)
    .map(toWizardStep);

  return { ...normalized, wizardSteps };
};

/**
 * A date field's control holds a `Date` while the backend declares the box's `value` as an ISO
 * string, so those two are compared as instants rather than by identity.
 */
const sameValue = (declared: unknown, current: unknown): boolean =>
  current instanceof Date ? new Date(String(declared)).getTime() === current.getTime() : declared === current;

/** The declared boxes that apply to `value`; a box without a `value` of its own always applies. */
export const infoBoxesForValue = (boxes: WorkflowInfoBox[] | undefined, value: unknown): WorkflowInfoBox[] =>
  (boxes ?? []).filter(box => box.value === undefined || sameValue(box.value, value));

/** Form fields in the order the backend asked for, each carrying its own key */
export const orderedFormFields = (
  formFields: Record<string, WorkflowWizardFormField> | undefined,
): WorkflowWizardFormFieldWithKey[] =>
  Object.entries(formFields ?? {})
    .map(([key, field]) => ({ ...field, key }))
    .toSorted((a, b) => (a.metaData?.order ?? 0) - (b.metaData?.order ?? 0));

/**
 * A wizard fully driven by the workflow response: every step, field, table and translation key
 * comes from the backend. See docs/workflow-wizard-contract.md for the contract.
 */
/**
 * The action name from the URL: the wizard shell sits under `:actionName`, so the parameter belongs
 * to an ancestor route rather than the shell's own.
 */
export const actionNameFromRoute = (snapshot: ActivatedRouteSnapshot): string | undefined =>
  snapshot.pathFromRoot.map(route => route.params[WORKFLOW_WIZARD_ACTION_PARAM]).find(isTruthy);

@Injectable()
export class WorkflowWizardApi extends SelfServiceWorkflowApi<WorkflowWizard> {
  override actionName =
    inject(WORKFLOW_WIZARD, { optional: true }) ??
    requiredValue(actionNameFromRoute(inject(ActivatedRoute).snapshot), 'workflow wizard action name');

  override mapToModel(response: WorkflowIntegrationResponse): WorkflowWizard {
    return toWorkflowWizardModel(response);
  }
}
